import * as L from 'leaflet';
import { BehaviorSubject, Observable } from 'rxjs';

export type WmsReadiness = 'pending' | 'ready' | 'failed' | 'removed';
const readiness = new WeakMap<L.Layer, BehaviorSubject<WmsReadiness>>();

/** Attach before adding the layer to the map, so early failures are retained. */
export function trackWmsReadiness(layer: L.TileLayer): Observable<WmsReadiness> {
  const existing = readiness.get(layer);
  if (existing) return existing.asObservable();
  const state = new BehaviorSubject<WmsReadiness>('pending');
  readiness.set(layer, state);
  const tiles = new Map<HTMLElement, 'pending' | 'ready' | 'failed'>();
  const evaluate = () => {
    // Leaflet retains old zoom levels during transitions; only current tiles gate WFS.
    const grid = (layer as any)._tiles;
    const current: HTMLElement[] = grid
      ? Object.values(grid).filter((tile: any) => tile.current).map((tile: any) => tile.el)
      : Array.from(tiles.keys());
    if (current.some((tile) => tiles.get(tile) === 'failed')) {
      state.next('failed');
    } else if (current.length && !layer.isLoading() && current.every((tile) => tiles.get(tile) === 'ready')) {
      state.next('ready');
    } else {
      state.next('pending');
    }
  };
  layer.on('loading', () => state.next('pending'));
  layer.on('tileloadstart', (event: L.TileEvent) => {
    tiles.set(event.tile, 'pending');
    state.next('pending');
  });
  layer.on('tileload', (event: L.TileEvent) => {
    tiles.set(event.tile, 'ready');
    evaluate();
  });
  layer.on('tileerror', (event: L.TileErrorEvent) => {
    tiles.set(event.tile, 'failed');
    evaluate();
  });
  layer.on('tileunload', (event: L.TileEvent) => {
    tiles.delete(event.tile);
    evaluate();
  });
  layer.on('load', evaluate);
  layer.on('add', evaluate);
  layer.on('remove', () => {
    tiles.clear();
    state.next('removed');
  });
  return state.asObservable();
}

export function getWmsReadiness(layer?: L.Layer): Observable<WmsReadiness> | undefined {
  return layer ? readiness.get(layer)?.asObservable() : undefined;
}

/** Check the current tracked viewport without starting another readiness subscription. */
export function isWmsReady(layer?: L.Layer): boolean {
  return !!layer && readiness.get(layer)?.value === 'ready';
}
